
import React, { useEffect, useState } from 'react';
import Header from './Header';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
}

const Layout: React.FC<LayoutProps> = ({ 
  children, 
  fullWidth = false,
  className 
}) => {
  const [mounted, setMounted] = useState(false);

  // After mount, add the animation classes
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className={cn(
      "flex flex-col min-h-screen relative overflow-hidden will-change-auto",
      className
    )}>
      {/* Base background - softer dark blue instead of harsh black */}
      <div className="fixed inset-0 bg-[#192035] -z-10" />
      
      {/* Animated subtle gradient overlay with reduced opacity */}
      <div className="fixed inset-0 opacity-30 -z-10">
        <div className="absolute inset-0 bg-gradient-to-r from-[#192035]/95 via-[#232841]/95 to-[#1E2235]/95"></div>
        <div className="absolute top-0 right-0 w-2/3 h-2/3 opacity-15 bg-gradient-radial from-neon-purple/15 via-transparent to-transparent animate-pulse-soft"></div>
        <div className="absolute bottom-0 left-0 w-2/3 h-2/3 opacity-15 bg-gradient-radial from-neon-blue/15 via-transparent to-transparent animate-pulse-soft"></div>
      </div>
      
      {/* Circuit pattern for tech feel - reduced opacity */}
      <div className="fixed inset-0 bg-circuit-pattern opacity-3 -z-10" />
      
      {/* Subtle grid for depth - reduced opacity */}
      <div className="fixed inset-0 bg-cyber-grid bg-[length:50px_50px] opacity-3 -z-10" />
      
      {/* Animated particle effect - reduced intensity */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute w-1 h-1 rounded-full bg-neon-purple/20 top-1/4 left-1/3 animate-floating blur-sm"></div>
        <div className="absolute w-1 h-1 rounded-full bg-neon-blue/20 top-3/4 left-2/3 animate-floating blur-sm" style={{ animationDelay: '1s' }}></div>
        <div className="absolute w-1 h-1 rounded-full bg-neon-green/20 top-2/3 left-1/4 animate-floating blur-sm" style={{ animationDelay: '2s' }}></div>
        <div className="absolute w-1 h-1 rounded-full bg-neon-pink/20 top-1/2 left-3/4 animate-floating blur-sm" style={{ animationDelay: '1.5s' }}></div>
      </div>
      
      <Header />
      
      <main className={cn(
        "flex-1 pt-20 pb-24 z-0",
        fullWidth ? 'w-full' : '',
        mounted ? 'animate-fade-in' : 'opacity-0',
      )}>
        {children}
      </main>
    </div>
  );
};

export default Layout;
